import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Button, TextField, Chip, IconButton, Grid, Paper, Pagination, PaginationItem } from "@mui/material";

// import Chip from "@mui/material/Chip";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useDispatch, useSelector } from "react-redux";
import {
    getAllAreasAction,
    clearGetAllAreasAction,
} from "../../../store/actions";
import AddArea from "./AddArea";

const Area = () => {
    const [open, setOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedAreaId, setSelectedAreaId] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [page, setPage] = useState(1);
    const rowsPerPage = 10;
    const dispatch = useDispatch();

    const { successData: areasData, loading: areasLoading } = useSelector((state) => state.getAllAreasReducer || {});

    const normalizedAreas = useMemo(() => {
        const arr = Array.isArray(areasData)
            ? areasData
            : Array.isArray(areasData?.data?.areas)
            ? areasData.data.areas
            : [];
        return arr.map((a) => ({
            areaId: a.areaId,
            areaName: a.areaName,
            regionId: a.regionId,
            regionName: a.region?.region_name || "",
            zoneId: a.zoneId,
            zoneName: a.zone?.zone_name || "",
            status: a.status ?? "Active",
        }));
    }, [areasData]);

    const filteredAreas = useMemo(() => {
        const q = (searchQuery || "").toString().trim().toLowerCase();
        if (!q) return normalizedAreas;
        return normalizedAreas.filter((a) => {
            const id = String(a.areaId).toLowerCase();
            const areaName = (a.areaName || "").toLowerCase();
            const regionName = (a.regionName || "").toLowerCase();
            const zoneName = (a.zoneName || "").toLowerCase();
            const status = (a.status || "").toLowerCase();
            return (
                id.includes(q) ||
                areaName.includes(q) ||
                regionName.includes(q) ||
                zoneName.includes(q) ||
                status.includes(q)
            );
        });
    }, [normalizedAreas, searchQuery]);

    // Pagination derived values
    const totalPages = useMemo(() => {
        const pages = Math.ceil(filteredAreas.length / rowsPerPage);
        return Math.max(1, pages || 1);
    }, [filteredAreas.length]);

    const paginatedAreas = useMemo(() => {
        const start = (page - 1) * rowsPerPage;
        return filteredAreas.slice(start, start + rowsPerPage);
    }, [filteredAreas, page]);

    const showingStart = filteredAreas.length === 0 ? 0 : ((page - 1) * rowsPerPage + 1);
    const showingEnd = Math.min(page * rowsPerPage, filteredAreas.length);

    useEffect(() => {
        // Fetch areas on mount
        dispatch(clearGetAllAreasAction());
        dispatch(getAllAreasAction());
    }, [dispatch]);

    useEffect(() => {
        // Reset page when search filter changes
        setPage(1);
    }, [searchQuery]);

    useEffect(() => {
        // Clamp page if total pages shrink
        if (page > totalPages) {
            setPage(1);
        }
    }, [totalPages, page]);

    const handleAdd = () => {
        setIsEdit(false);
        setSelectedAreaId("");
        setOpen(true);
    };

    const handleEdit = (areaId) => {
        if (areaId === undefined || areaId === null) return;
        setIsEdit(true);
        setSelectedAreaId(String(areaId));
        setOpen(true);
    };

    const handleCloseDialog = () => {
        setOpen(false);
        setIsEdit(false);
        setSelectedAreaId("");
    };

    const getStatusChipStyles = (status) => {
        const s = (status || '').toString().toLowerCase();
        if (s === 'active') return { backgroundColor: '#D1FAE5', color: '#047857' };
        if (s === 'inactive') return { backgroundColor: '#FECACA', color: '#B91C1C' };
        if (s === 'pending') return { backgroundColor: '#FDE68A', color: '#92400E' };
        return { backgroundColor: '#E5E7EB', color: '#111827' };
    };

    return (
        <Box sx={{ minHeight: '100vh', width: '100%', mt: 2 }}>
            {/* Header */}
            <Typography fontSize={18} fontWeight={600} color="#111827" mb={2}>
                Area Management
            </Typography>

            {/* Search + Button */}
            <Box
                sx={{
                    display: "flex",
                    gap: 2,
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                    mb: 3,
                    p: 2,
                    backgroundColor: '#fff',
                    borderRadius: 3,
                    boxShadow: "0px 4px 12px rgba(0,0,0,0.08)",
                }}
            >
                <TextField
                    placeholder="Search ..."
                    size="small"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{
                        width: { xs: 300, sm: 300 },
                        '& .MuiOutlinedInput-root': { borderRadius: 3 },
                        '& .MuiInputBase-input': { borderRadius: 3 },
                    }}
                />

                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    sx={{
                        bgcolor: "#4F46E5",
                        textTransform: "none",
                        borderRadius: 3,
                        width:{xs:300},
                        px: 3,
                    }}
                    onClick={handleAdd}
                >
                    Add New Area
                </Button>
            </Box>

            {/* Desktop (sm+) grid header and rows (match Zone style) */}
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                {/* Header */}
                <Box
                    display={{ xs: 'none', sm: 'grid' }}
                    gridTemplateColumns={{ sm: '1fr 2fr 1fr 1fr 1fr 1fr' }}
                    alignItems="center"
                    px={{ xs: 1, sm: 2 }}
                    py={{ xs: 1, sm: 2 }}
                    mb={{ xs: 1, sm: 1 }}
                >
                    {['AREA ID', 'AREA NAME', 'REGION', 'ZONE', 'STATUS', 'ACTIONS'].map((label, i) => (
                        <Typography
                            key={label}
                            fontSize={{ sm: 10, md: 12 }}
                            color="#000000"
                            fontWeight={700}
                            sx={{ textTransform: 'uppercase', textAlign: i === 4 ? 'center' : i === 5 ? 'right' : 'left' }}
                        >
                            {label}
                        </Typography>
                    ))}
                </Box>

                {/* Rows */}
                {areasLoading && (
                    <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #E5E7EB', px: 2, py: 1.5, mb: 2 }}>
                        <Typography variant="body2">Loading areas...</Typography>
                    </Paper>
                )}
                {!areasLoading && paginatedAreas.map(({ areaId, areaName, regionName, zoneName, status }) => {
                    const chipSx = {
                        width: 90,
                        height: 24,
                        ...getStatusChipStyles(status),
                        '& .MuiChip-label': { fontSize: 12, fontWeight: 600 },
                    };
                    return (
                        <Paper
                            key={areaId || areaName}
                            elevation={0}
                            sx={{
                                borderRadius: 3,
                                display: { xs: 'none', sm: 'grid' },
                                gridTemplateColumns: { sm: '1fr 2fr 1fr 1fr 1fr 1fr' },
                                alignItems: 'center',
                                border: '1px solid #E5E7EB',
                                px: 2,
                                py: 1.5,
                                gap: 0,
                                columnGap: 0,
                                mb: 1.25,
                                '&:hover': {
                                    border: '2px solid #2563EB',
                                    boxShadow: '0 4px 14px rgba(37,99,235,0.18)',
                                },
                            }}
                        >
                            <Typography variant="body2" sx={{ textAlign: 'left' }}>{areaId}</Typography>
                            <Typography variant="body2" sx={{ textAlign: 'left' }}>{areaName}</Typography>
                            <Typography variant="body2" sx={{ textAlign: 'left' }}>{regionName}</Typography>
                            <Typography variant="body2" sx={{ textAlign: 'left' }}>{zoneName}</Typography>
                            <Box sx={{ justifySelf: 'center' }}>
                                <Chip label={String(status).charAt(0).toUpperCase() + String(status).slice(1)} size="small" sx={chipSx} />
                            </Box>
                            <Box display="flex" alignItems="center" gap={1} justifyContent="flex-end" sx={{ justifySelf: 'end' }}>
                                <IconButton size="small" aria-label="edit area" onClick={() => handleEdit(areaId)}>
                                    <EditOutlinedIcon fontSize="small" sx={{ color: '#2563EB', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                                </IconButton>
                            </Box>
                        </Paper>
                    );
                })}
            </Box>

            {/* Cards for xs screens */}
            <Grid container spacing={2} sx={{ display: { xs: 'flex', sm: 'none' } }} justifyContent="center">
                {!areasLoading && paginatedAreas.map(({ areaId, areaName, regionName, zoneName, status }) => {
                    return (
                    <Grid item xs={12} key={areaId || areaName} sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                borderRadius: 3,
                                width: 300,
                                backgroundColor: '#fff',
                                border: '1px solid #E5E7EB',
                                boxShadow: '0 4px 14px rgba(37,99,235,0.18)',
                                '&:hover': {
                                    border: '2px solid #2563EB',
                                    boxShadow: '0 4px 14px rgba(37,99,235,0.18)',
                                },
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
                                <Box />
                                <Chip
                                    label={status}
                                    size="small"
                                    sx={{ ...getStatusChipStyles(status), fontWeight: 600 }}
                                />
                            </Box>
                            <Box sx={{ mb: 1 }}>
                                <Typography variant="body2" fontWeight={600} color="#0F172A">Area: {areaName}</Typography>
                                <Typography variant="body2" fontWeight={600} color="#64748B">Area ID: {areaId}</Typography>
                                <Typography variant="body2" fontWeight={600} color="#64748B">Region: {regionName}</Typography>
                                <Typography variant="body2" fontWeight={600} color="#64748B">Zone: {zoneName}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                <IconButton size="small" onClick={() => handleEdit(areaId)}><EditOutlinedIcon fontSize="small" sx={{ color: '#2563EB' }} /></IconButton>
                            </Box>
                        </Paper>
                    </Grid>
                    );
                })}
            </Grid>

            {/* Footer Pagination */}
            {!areasLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                        Showing {showingStart}-{showingEnd} of {filteredAreas.length}
                    </Typography>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(e, value) => setPage(value)}
                        size="small"
                        renderItem={(item) => (
                            <PaginationItem
                                {...item}
                                sx={{
                                    borderRadius: '50%',
                                    minWidth: 32,
                                    height: 32,
                                    '&.Mui-selected': {
                                        backgroundColor: '#2563EB',
                                        color: '#FFFFFF',
                                    },
                                    '&.Mui-selected:hover': {
                                        backgroundColor: '#2563EB',
                                        color: '#FFFFFF',
                                    },
                                }}
                            />
                        )}
                    />
                </Box>
            )}

            <AddArea open={open} onClose={handleCloseDialog} isEdit={isEdit} areaId={selectedAreaId} />
        </Box>
    );
};

export default Area;
